import { PromiseResolvers } from '@nutrien/minesight-utility-module';

import { HasKind } from '@/utilities/HasKind';

// Goals (in the context of Borer) are special types of actions that have extra qualities:
//   * They are (often) dispatched from asynchronous functions that lives outside of the replication system (including by UI controls)
//   * They contain a `PromiseResolvers` that lets them:
//     * Return a `Promise` (or just `await` it) from the async function that dispatched it.
//     * Provide `reject` and `resolve` to the replication system to settle that `Promise`.
//       * Which generally happens in the reducers.
//   * Their `PromiseResovlers` are aggregated in `GoalResolvers` which maps to their `kind`.
//     * Which then lets the reducers determine the behaviour throughout the replication life cycle.
//     * Note that there can be multiple `Goals` in play at any given time;
//         it's the job of the reducers to manage this.
//
// Under legacy replication, `RxdbManager` exposed the mechanics of replication
// to all other code (including UI controls) via async functions.
// This meant that any code could trigger any aspect of the replication life cycle at any time,
// including while other aspects were already in progress.
// `DataManagerHandler` removes that adverse potential via `useReducer`,
// but the downstream code still expects to be able to call the legacy async functions.
// Goals help bridge that gap by creating `Promise`s that are settled as the life cycle evolves.
// They represent what the downstream code _wants_ to have happen,
// not what _will_ happen (and, more importantly, _when_ it will happen).
export interface Goal<K extends string> extends HasKind<K> {
  resolvers: PromiseResolvers<void>;
}

export function createGoal<K extends string>(kind: K): Goal<K> {
  const resolvers = Promise.withResolvers<void>();
  return {
    kind,
    resolvers,
  };
}
